/**
 * Full-bleed cinematic hero for the /preview facelift.
 * Uses Tammy's real storefront photo with a slow Ken Burns zoom.
 */

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import UberEatsOrderLink from '../../components/UberEatsOrderLink';
import { PREVIEW_HERO_DESCRIPTION, PREVIEW_HERO_IMAGE, PREVIEW_HERO_TITLE, PREVIEW_TRUST_CHIPS } from './previewAssets';
import type { SeasonalHeroCopy } from './seasonalHero';
import type { PreviewContact } from './usePreviewHomeData';
import './previewHome.css';

export default function PreviewHero({
  contact,
  seasonal,
}: {
  contact: PreviewContact;
  /** From Admin → Seasons. Blank fields keep the approved default sentence. */
  seasonal?: SeasonalHeroCopy | null;
}) {
  const tel = contact.phone.replace(/\D/g, '');
  const title = seasonal?.title || PREVIEW_HERO_TITLE;
  const description = seasonal?.description || PREVIEW_HERO_DESCRIPTION;

  return (
    <section className="relative min-h-[88vh] overflow-hidden text-white">
      {/* Ken Burns photo — this still is the "video" for v1. */}
      <img
        src={PREVIEW_HERO_IMAGE}
        alt="Nanny B's Bakery storefront at 62 Commerce Park Drive in Barrie"
        className="preview-kenburns absolute inset-0 h-full w-full object-cover object-[center_30%]"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-primary-900/85 via-primary-900/70 to-primary-800/35" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-primary-900/20" />

      <div className="container-custom relative z-10 flex min-h-[88vh] items-end pb-16 pt-28 md:items-center md:pb-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="max-w-2xl"
        >
          <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/30 bg-black/45 px-4 py-1.5 text-sm text-white backdrop-blur-sm">
            <span className="h-2 w-2 rounded-full bg-emerald-400" />
            {contact.hours}
          </p>
          {seasonal?.badge ? (
            <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-amber-200">
              {seasonal.badge}
            </p>
          ) : null}
          <h1 className="font-display text-4xl font-bold leading-tight text-balance md:text-6xl">
            {title}
          </h1>
          {/* Default sentence is Tammy's. A live season can replace it from Admin → Seasons. */}
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-white/90 md:text-xl">
            {description}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/order" className="btn-secondary bg-white text-primary-700 hover:bg-bakery-cream">
              Order for pickup
            </Link>
            <Link
              to="/menu"
              className="btn-outline border-white bg-black/30 text-white hover:bg-white hover:text-primary-700"
            >
              View menu
            </Link>
            <UberEatsOrderLink variant="hero" />
          </div>

          <ul className="mt-8 flex flex-wrap gap-2">
            {PREVIEW_TRUST_CHIPS.map((chip) => (
              <li
                key={chip}
                className="rounded-full border border-white/20 bg-black/20 px-3 py-1 text-xs font-medium tracking-wide text-white/90"
              >
                {chip}
              </li>
            ))}
          </ul>

          <p className="mt-6 text-sm text-white/80">
            Call{' '}
            <a className="underline decoration-white/40 underline-offset-4 hover:text-white" href={`tel:${tel}`}>
              {contact.phone}
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
}
